import { useEffect, useRef, type CSSProperties, type PointerEvent } from "react";
import type { BoardId, WhiteboardLayout, WhiteboardStroke } from "./api";

// A small corner picture-in-picture box (legend or board).
const PIP: CSSProperties = {
  position: "absolute",
  width: "30%",
  minWidth: 110,
  aspectRatio: "16 / 9",
  height: "auto",
  bottom: "4%",
  right: "4%",
  borderRadius: "var(--radius-sm)",
  border: "2px solid var(--gold)",
  overflow: "hidden",
  boxShadow: "var(--shadow-pop)",
  zIndex: 3,
  background: "#000",
};
const HALF_L: CSSProperties = { position: "absolute", left: 0, top: 0, bottom: 0, width: "50%" };
const FILL: CSSProperties = { position: "absolute", inset: 0, width: "100%", height: "100%" };

/** whiteboardLayout maps a layout to the absolute positions of the legend feed
 *  and the board within a positioned stage (ADR 0022). Apply `.video` to the
 *  source <video> and `.board` to the whiteboard box. */
export function whiteboardLayout(layout: WhiteboardLayout = "pip-legend"): {
  video: CSSProperties;
  board: CSSProperties;
} {
  switch (layout) {
    case "full":
      return { video: { ...FILL, opacity: 0 }, board: { ...FILL } };
    case "pip-board":
      return { video: { ...FILL }, board: { ...PIP } };
    case "side":
      // LSC-628: the legend keeps the LEFT half; the board sits in the RIGHT half.
      // A full-height right-half box (HALF_R) is 8:9 = 0.889:1 PORTRAIT on a 16:9
      // stage, and the court/slate is drawn stretched-to-fill that box — squashing a
      // 2.25:1 gridiron ~2.5× (and replaying strokes non-uniformly, since they
      // normalize to the box, so a circle → a tall ellipse). Letterbox the board to
      // its natural 16:9 inside the right half, vertically centered: correctness over
      // size (the board is smaller, but the side-by-side split is preserved, and the
      // canvas — court AND strokes — is now a true 16:9 region). maxHeight guards an
      // off-nominal (wider-than-16:9) stage.
      return {
        video: { ...HALF_L },
        board: {
          position: "absolute",
          right: 0,
          top: "50%",
          transform: "translateY(-50%)",
          width: "50%",
          aspectRatio: "16 / 9",
          height: "auto",
          maxHeight: "100%",
        },
      };
    case "pip-legend":
    default:
      return { video: { ...PIP }, board: { ...FILL } };
  }
}

// A real whiteboard (ADR 0022): a freehand drawing surface shared through the
// program state. The director draws (editable); viewers render read-only.
// Coordinates are normalized 0..1 so the drawing scales to any surface size.

export interface WhiteboardCanvasProps {
  strokes: WhiteboardStroke[];
  /** The board painted behind the strokes (ADR 0045). A built-in vector template
   *  (blank/football/…) OR — when it names an admin-uploaded slate id (ADR 0058) —
   *  the id itself; in that case the resolved `boardImageUrl` is what's drawn and
   *  this falls back to the blank vector board. Rendered for everyone (Director,
   *  Legend, Fan); it rides the relayed program state. */
  board?: BoardId;
  /** When set, an admin-uploaded slate image drawn as the background UNDER the
   *  strokes (ADR 0058), replacing the vector `board`. Resolve it from `board`
   *  via resolveBoardImageUrl (boardSlates.tsx). */
  boardImageUrl?: string;
  editable?: boolean;
  onChange?: (strokes: WhiteboardStroke[]) => void;
  color?: string;
  penWidth?: number;
  ariaLabel?: string;
  style?: CSSProperties;
}

// A warm off-white board so the gold pen reads on it — and, critically, so an EMPTY
// board never reads as a dead feed. A board the colour of the dark stage/cave ground
// is the specific wrong answer (cross-platform lesson, iOS LSC-382: QA reported a
// blank board "renders as a black surface"). The web fills this unconditionally
// (drawBoard/WhiteboardCanvas always paint it, blank if empty — there is no
// undrawn-region path), and `Whiteboard.test.ts` pins it LIGHT by relative luminance
// so it can't silently regress to a dark value (the way a comment-only constant did
// on iOS).
export const BOARD_BG = "#f4efe4";

// The vector board templates (ADR 0045; soccer + hockey added by ADR 0057). No
// image assets — each is drawn onto the 2D canvas beneath the strokes, sized to
// the backing store so it scales with the surface. Colours are chosen to sit
// inside the Cave language (turf/hardwood/ice with gold-tinted end zones and cream
// lines) without pulling in CSS-var lookups on the hot draw path.
// Base field colours are NAMED + exported so the contrast guard walks the REAL
// surfaces a stroke sits on, not copied literals (a literal in a test is the failure
// this whole thread was about). Note: blank AND hockey ICE are LIGHT surfaces where
// the default gold pen alone fails — see PEN_OUTLINE.
// Football + soccer pitch. Reconciled DARK across clients (LSC-483): the old #20563a
// cleared the 3.0 bar only narrowly (gold 3.54, +0.54) — the outlier of the set, and it
// failed the stricter 4.5 bar the native guard uses. #16321c (shared with the clients)
// clears with real margin (gold 5.76) at BOTH bars, so the palette is jointly-validated.
const TURF = "#16321c"; // football + soccer — dark (gold pen carries, big margin)
// Basketball hardwood. Reconciled DARK across clients (LSC-483): the old #7c5230 was a
// mid-tone where NEITHER the gold pen (2.79) nor the dark outline (2.88) cleared the
// 3.0 graphical bar — the dead-zone floor is ~#5a5a5a/2.85. #3a2617 (shared with the
// native clients) is dark enough that the gold pen reads (5.91) with real margin.
const HARDWOOD = "#3a2617"; // basketball — dark (gold pen carries)
const ICE = "#dfe8ef"; // hockey — LIGHT (dark outline carries; ice should look like ice)
export const BOARD_SURFACES: Record<string, string> = { blank: BOARD_BG, turf: TURF, hardwood: HARDWOOD, ice: ICE };

// A dark halo drawn UNDER every pen stroke (LSC-480) so a bright pen — the default
// gold — reads on a LIGHT board too: the blank board AND the hockey ice are both
// light, where gold's fill contrast is ~2:1 (below the bar). On the dark fields the
// coloured pen carries the contrast and the halo is harmlessly invisible; on light
// surfaces the halo's near-black edge is what makes the stroke read. Board-agnostic:
// reads on ANY surface with no per-board branch (the option chosen over dark-everywhere,
// which couldn't help the ice, and over a board-lightness-aware pen, which would need
// per-board logic in every client).
export const PEN_OUTLINE = "#0b0d10";

const LINE = "rgba(245,239,228,0.74)"; // cream hairline (echoes --stone lines)
const GOLD_TINT = "rgba(201,162,39,0.30)"; // brand gold, low alpha

function drawFootball(ctx: CanvasRenderingContext2D, w: number, h: number) {
  ctx.fillStyle = TURF; // turf
  ctx.fillRect(0, 0, w, h);
  const ez = w / 12; // end-zone width
  const play = w - ez * 2;
  const band = play / 10; // ten 10-yard bands
  for (let i = 0; i < 10; i++) {
    ctx.fillStyle = i % 2 === 0 ? "#245c3f" : "#1e5036"; // mow stripes
    ctx.fillRect(ez + i * band, 0, band, h);
  }
  ctx.fillStyle = GOLD_TINT; // end zones
  ctx.fillRect(0, 0, ez, h);
  ctx.fillRect(w - ez, 0, ez, h);
  ctx.strokeStyle = LINE;
  ctx.lineWidth = Math.max(1, w / 340);
  for (let i = 0; i <= 10; i++) {
    const x = ez + i * band;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
    ctx.stroke();
  }
  // Heavier goal lines.
  ctx.lineWidth = Math.max(1.5, w / 210);
  for (const x of [ez, w - ez]) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
    ctx.stroke();
  }
  // Two rows of hash-mark ticks.
  ctx.lineWidth = Math.max(1, w / 520);
  const tick = h * 0.035;
  for (const y of [h * 0.34, h * 0.66]) {
    for (let i = 0; i <= 10; i++) {
      const x = ez + i * band;
      ctx.beginPath();
      ctx.moveTo(x, y - tick / 2);
      ctx.lineTo(x, y + tick / 2);
      ctx.stroke();
    }
  }
}

function drawBasketball(ctx: CanvasRenderingContext2D, w: number, h: number) {
  ctx.fillStyle = HARDWOOD; // hardwood
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = LINE;
  ctx.lineWidth = Math.max(1, w / 340);
  const pad = Math.min(w, h) * 0.05;
  const cx = w / 2;
  const cy = h / 2;
  const r = Math.min(w, h) * 0.13; // centre circle
  ctx.strokeRect(pad, pad, w - pad * 2, h - pad * 2); // boundary
  ctx.beginPath(); // half-court line
  ctx.moveTo(cx, pad);
  ctx.lineTo(cx, h - pad);
  ctx.stroke();
  ctx.beginPath(); // centre circle
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
  const keyH = (h - pad * 2) * 0.4;
  const keyW = (w - pad * 2) * 0.15;
  const keyY = cy - keyH / 2;
  const arc = keyH * 0.34;
  const three = (h - pad * 2) * 0.46;
  // Left basket end.
  ctx.strokeRect(pad, keyY, keyW, keyH);
  ctx.beginPath();
  ctx.arc(pad + keyW, cy, arc, -Math.PI / 2, Math.PI / 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(pad, cy, three, -Math.PI / 2.7, Math.PI / 2.7);
  ctx.stroke();
  // Right basket end (mirror).
  ctx.strokeRect(w - pad - keyW, keyY, keyW, keyH);
  ctx.beginPath();
  ctx.arc(w - pad - keyW, cy, arc, Math.PI / 2, (Math.PI * 3) / 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(w - pad, cy, three, Math.PI - Math.PI / 2.7, Math.PI + Math.PI / 2.7);
  ctx.stroke();
}

function drawSoccer(ctx: CanvasRenderingContext2D, w: number, h: number) {
  ctx.fillStyle = TURF; // pitch turf (matches the gridiron family)
  ctx.fillRect(0, 0, w, h);
  const band = w / 10; // faint mow stripes across the pitch
  for (let i = 0; i < 10; i++) {
    ctx.fillStyle = i % 2 === 0 ? "#245c3f" : "#1e5036";
    ctx.fillRect(i * band, 0, band, h);
  }
  ctx.strokeStyle = LINE;
  ctx.lineWidth = Math.max(1, w / 340);
  const pad = Math.min(w, h) * 0.05;
  const cx = w / 2;
  const cy = h / 2;
  const r = Math.min(w, h) * 0.13; // centre circle
  const spot = Math.max(1.5, w / 200);
  ctx.strokeRect(pad, pad, w - pad * 2, h - pad * 2); // touch + goal lines
  ctx.beginPath(); // halfway line
  ctx.moveTo(cx, pad);
  ctx.lineTo(cx, h - pad);
  ctx.stroke();
  ctx.beginPath(); // centre circle
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath(); // centre spot (a small ring, drawn like the other markings)
  ctx.arc(cx, cy, spot, 0, Math.PI * 2);
  ctx.stroke();
  const boxH = (h - pad * 2) * 0.6; // penalty area
  const boxW = (w - pad * 2) * 0.16;
  const boxY = cy - boxH / 2;
  const gaH = (h - pad * 2) * 0.28; // goal area (six-yard box)
  const gaW = (w - pad * 2) * 0.06;
  const gaY = cy - gaH / 2;
  const penX = boxW * 0.66; // penalty spot depth from the goal line
  const penArc = boxH * 0.36; // penalty (D) arc radius
  // Left end.
  ctx.strokeRect(pad, boxY, boxW, boxH);
  ctx.strokeRect(pad, gaY, gaW, gaH);
  ctx.beginPath(); // penalty spot
  ctx.arc(pad + penX, cy, spot, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath(); // penalty (D) arc
  ctx.arc(pad + penX, cy, penArc, -Math.PI / 2.6, Math.PI / 2.6);
  ctx.stroke();
  // Right end (mirror).
  ctx.strokeRect(w - pad - boxW, boxY, boxW, boxH);
  ctx.strokeRect(w - pad - gaW, gaY, gaW, gaH);
  ctx.beginPath();
  ctx.arc(w - pad - penX, cy, spot, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(w - pad - penX, cy, penArc, Math.PI - Math.PI / 2.6, Math.PI + Math.PI / 2.6);
  ctx.stroke();
}

function drawHockey(ctx: CanvasRenderingContext2D, w: number, h: number) {
  const RED = "rgba(200,52,44,0.85)";
  const BLUE = "rgba(58,110,196,0.85)";
  ctx.fillStyle = ICE; // ice
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = LINE;
  ctx.lineWidth = Math.max(1, w / 340);
  const pad = Math.min(w, h) * 0.05;
  const cx = w / 2;
  const cy = h / 2;
  const r = Math.min(w, h) * 0.13; // centre faceoff circle
  const spot = Math.max(1.5, w / 220);
  ctx.strokeRect(pad, pad, w - pad * 2, h - pad * 2); // boards (rounded feel omitted)
  // Centre red line + two blue lines.
  ctx.lineWidth = Math.max(1.5, w / 150);
  const lines: Array<[number, string]> = [
    [cx, RED],
    [cx - (w - pad * 2) * 0.2, BLUE],
    [cx + (w - pad * 2) * 0.2, BLUE],
  ];
  for (const [x, colour] of lines) {
    ctx.strokeStyle = colour;
    ctx.beginPath();
    ctx.moveTo(x, pad);
    ctx.lineTo(x, h - pad);
    ctx.stroke();
  }
  // Centre faceoff circle + spot.
  ctx.strokeStyle = BLUE;
  ctx.lineWidth = Math.max(1, w / 340);
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath(); // centre faceoff spot (a small ring)
  ctx.arc(cx, cy, spot, 0, Math.PI * 2);
  ctx.stroke();
  // Four end-zone faceoff circles + a goal line & crease near each end.
  ctx.strokeStyle = RED;
  const fx = (w - pad * 2) * 0.12; // circle inset from the goal line
  const fy = (h - pad * 2) * 0.24; // circle inset from centre
  const fr = Math.min(w, h) * 0.1;
  const goalX = (w - pad * 2) * 0.06; // goal line depth
  const crease = Math.min(w, h) * 0.06;
  for (const ex of [pad + goalX, w - pad - goalX]) {
    ctx.lineWidth = Math.max(1.5, w / 260);
    ctx.beginPath(); // goal line
    ctx.moveTo(ex, pad);
    ctx.lineTo(ex, h - pad);
    ctx.stroke();
    ctx.lineWidth = Math.max(1, w / 340);
    const dir = ex < cx ? 1 : -1; // crease opens toward centre ice
    ctx.beginPath();
    ctx.arc(ex, cy, crease, dir === 1 ? -Math.PI / 2 : Math.PI / 2, dir === 1 ? Math.PI / 2 : (Math.PI * 3) / 2);
    ctx.stroke();
  }
  for (const ex of [pad + goalX + fx, w - pad - goalX - fx]) {
    for (const ey of [cy - fy, cy + fy]) {
      ctx.beginPath();
      ctx.arc(ex, ey, fr, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath(); // faceoff spot (a small ring)
      ctx.arc(ex, ey, spot, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
}

function drawBoard(ctx: CanvasRenderingContext2D, w: number, h: number, board: BoardId | undefined) {
  if (board === "football") return drawFootball(ctx, w, h);
  if (board === "basketball") return drawBasketball(ctx, w, h);
  if (board === "soccer") return drawSoccer(ctx, w, h);
  if (board === "hockey") return drawHockey(ctx, w, h);
  // "blank", absent, or an unresolved uploaded-slate id → the warm off-white slate
  // (pre-0045 look). An uploaded slate is drawn from its image instead (see draw).
  ctx.fillStyle = BOARD_BG;
  ctx.fillRect(0, 0, w, h);
}

function draw(
  canvas: HTMLCanvasElement,
  strokes: WhiteboardStroke[],
  board: BoardId | undefined,
  boardImg: HTMLImageElement | null,
  live?: WhiteboardStroke,
) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const { width, height } = canvas;
  ctx.clearRect(0, 0, width, height);
  if (boardImg) {
    // An admin-uploaded slate image (ADR 0058), drawn UNDER the strokes. A neutral
    // ground fills first so a not-yet-decoded image never flashes transparent.
    ctx.fillStyle = BOARD_BG;
    ctx.fillRect(0, 0, width, height);
    try {
      ctx.drawImage(boardImg, 0, 0, width, height);
    } catch {
      // inert canvas / undecoded image under jsdom — the ground fill stands in
    }
  } else {
    drawBoard(ctx, width, height, board);
  }
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  const scale = width / 640;
  const paint = (s: WhiteboardStroke) => {
    if (s.points.length === 0) return;
    // Build the path once.
    ctx.beginPath();
    s.points.forEach((p, i) => {
      const x = p.x * width;
      const y = p.y * height;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    // a single dot renders as a small round mark
    if (s.points.length === 1) {
      const p = s.points[0];
      ctx.lineTo(p.x * width + 0.01, p.y * height);
    }
    const core = Math.max(1, s.width) * scale; // scale pen to board size
    // Two passes (LSC-480): a dark halo UNDER the coloured pen so the stroke reads on a
    // LIGHT board (blank + hockey ice) as well as the dark fields. Same path, stroked
    // wide-dark then narrow-colour.
    ctx.strokeStyle = PEN_OUTLINE;
    ctx.lineWidth = core + 3 * scale;
    ctx.stroke();
    ctx.strokeStyle = s.color;
    ctx.lineWidth = core;
    ctx.stroke();
  };
  strokes.forEach(paint);
  if (live) paint(live);
}

export default function WhiteboardCanvas({
  strokes,
  board = "blank",
  boardImageUrl,
  editable = false,
  onChange,
  color = "#1a1a1a",
  penWidth = 4,
  ariaLabel = "Whiteboard",
  style,
}: WhiteboardCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const liveRef = useRef<WhiteboardStroke | null>(null);
  const drawingRef = useRef(false);
  // The decoded slate image (ADR 0058) — null until loaded (or when there's no
  // boardImageUrl), so the draw path falls back to the vector board meanwhile.
  const boardImgRef = useRef<HTMLImageElement | null>(null);

  const repaint = () => {
    if (canvasRef.current) {
      draw(canvasRef.current, strokes, board, boardImgRef.current, liveRef.current ?? undefined);
    }
  };

  // Load the admin-uploaded slate image (ADR 0058) when boardImageUrl changes, and
  // repaint once it decodes. Clearing the url drops back to the vector board.
  useEffect(() => {
    if (!boardImageUrl) {
      boardImgRef.current = null;
      repaint();
      return;
    }
    let alive = true;
    const img = new Image();
    img.onload = () => {
      if (!alive) return;
      boardImgRef.current = img;
      repaint();
    };
    img.src = boardImageUrl;
    return () => {
      alive = false;
    };
    // strokes/board are covered by the resize effect below; this effect owns the image.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardImageUrl]);

  // Keep the backing store sized to the element (crisp lines), and repaint on
  // stroke, board, or size change.
  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.max(1, Math.round(rect.width * dpr));
      canvas.height = Math.max(1, Math.round(rect.height * dpr));
      draw(canvas, strokes, board, boardImgRef.current, liveRef.current ?? undefined);
    };
    resize();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(resize) : null;
    ro?.observe(wrap);
    return () => ro?.disconnect();
  }, [strokes, board, boardImageUrl]);

  const norm = (e: PointerEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)),
    };
  };

  const onDown = (e: PointerEvent) => {
    if (!editable) return;
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    drawingRef.current = true;
    liveRef.current = { color, width: penWidth, points: [norm(e)] };
    if (canvasRef.current) draw(canvasRef.current, strokes, board, boardImgRef.current, liveRef.current);
  };
  const onMove = (e: PointerEvent) => {
    if (!editable || !drawingRef.current || !liveRef.current) return;
    liveRef.current.points.push(norm(e));
    if (canvasRef.current) draw(canvasRef.current, strokes, board, boardImgRef.current, liveRef.current);
  };
  const onUp = () => {
    if (!editable || !drawingRef.current) return;
    drawingRef.current = false;
    const stroke = liveRef.current;
    liveRef.current = null;
    if (stroke && stroke.points.length > 0) onChange?.([...strokes, stroke]);
  };

  return (
    <div
      ref={wrapRef}
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: "16 / 9",
        borderRadius: "var(--radius-md)",
        overflow: "hidden",
        border: "1px solid var(--border-mute)",
        background: BOARD_BG,
        ...style,
      }}
    >
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={ariaLabel}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
        style={{ width: "100%", height: "100%", display: "block", touchAction: "none", cursor: editable ? "crosshair" : "default" }}
      />
    </div>
  );
}
